import { useState } from 'react';
import StyledSnackbar from 'src/components/StyledComponents/StyledSnackBar';
import { Product } from 'src/models/Management.interface';
import { apiUrls, errorMessages } from 'src/utils/properties.utils';
import { axiosTokenInstance } from 'src/utils/axiosInstance.utils';

interface PaginatedProductResponse {
  data: Product[];
  totalItems: number;
  currentPage: number;
  totalPages: number;
}

export const useGetProduct = () => {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const { showSnackbar } = StyledSnackbar();

  const fetchProducts = async (
    page = 1,
    limit = 15,
    search = '',
    sortBy = 'displayOrder',
    order = 'ASC'
  ) => {
    setLoading(true);
    try {
      const response = await axiosTokenInstance.get<PaginatedProductResponse>(
        apiUrls.PRODUCT_MANAGEMENT,
        {
          params: {
            offset: page,
            limit: limit,
            search: search,
            sortBy: sortBy,
            order: order,
          },
          withCredentials: true,
        }
      );

      // Extract data from paginated response
      setProducts(response.data.data || []);
      setTotalItems(response.data.totalItems || 0);
      setTotalPages(response.data.totalPages || 0);
      setCurrentPage(response.data.currentPage || 1);
    } catch (error: any) {
      if (error.response?.status === 404) {
        console.error(error.response.data.message);
        setProducts([]);
      } else {
        showSnackbar(errorMessages.SOMETHING_WENT_WRONG, 'error');
      }
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 800);
    }
  };

  const deleteProduct = async (id: string) => {
    try {
      const response = await axiosTokenInstance.delete(apiUrls.DELETE_PRODUCT, {
        params: { id },
      });

      showSnackbar(response.data.message, 'success');
    } catch (error) {
      showSnackbar(errorMessages.SOMETHING_WENT_WRONG, 'error');
    }
  };

  return {
    loading,
    products,
    totalItems,
    totalPages,
    currentPage,
    fetchProducts,
    deleteProduct,
  };
};

export const getUnmappedBasicProducts = async () => {
  const response = await axiosTokenInstance.get(
    apiUrls.PRODUCT_UNMAPPED_ACTIVE
  );
  return response.data.data;
};

export const getBasicProducts = async () => {
  const response = await axiosTokenInstance.get(apiUrls.PRODUCT_LIST_BASIC);
  return response.data.data;
};

export const productActivation = async (id: string, isActive: boolean) => {
  const response = await axiosTokenInstance.patch(apiUrls.PRODUCT_ACTIVATION, {
    id,
    isActive,
  });
  return response.data;
};

export interface ProductLog {
  id: string;
  productId: string;
  productName: string;
  beforeQuantity: number;
  changedQuantity: number;
  afterQuantity: number;
  logType: string;
  createdAt: string;
  logTypeReference: string;
}

export interface ProductLogsResponse {
  data: ProductLog[];
  totalItems: number;
}

export interface ProductLogsParams {
  productId: string;
  startCreatedDate?: string;
  endCreatedDate?: string;
  logType?: number;
  offset?: number;
  limit?: number;
  sortBy?: string;
  order?: 'ASC' | 'DESC';
}

export const fetchProductLogs = async (
  params: ProductLogsParams
): Promise<ProductLogsResponse> => {
  const queryParams: Record<string, string | number> = {
    productId: params.productId,
  };

  if (params.startCreatedDate) {
    queryParams.startCreatedDate = params.startCreatedDate;
  }
  if (params.endCreatedDate) {
    queryParams.endCreatedDate = params.endCreatedDate;
  }
  if (params.logType !== undefined) {
    queryParams.logType = params.logType;
  }
  if (params.offset !== undefined) {
    queryParams.offset = params.offset;
  }
  if (params.limit !== undefined) {
    queryParams.limit = params.limit;
  }
  if (params.sortBy) {
    queryParams.sortBy = params.sortBy;
  }
  if (params.order) {
    queryParams.order = params.order;
  }

  const response = await axiosTokenInstance.get<ProductLogsResponse>(
    apiUrls.PRODUCT_LOGS,
    { params: queryParams }
  );
  return response.data;
};
